import { h, Fragment } from 'preact';
import { formatMoney } from 'utils/storefront/format-resource';
import { useEffect, useMemo, useState, useRef } from 'preact/hooks';
import Icon from 'utils/icon';
import Image from 'utils/image';
import useResponsive from 'utils/responsive';
import isValidEmailCheck from 'utils/email-validation';
import { getVariantDataById } from '../utils/check-variant-in-stock';
import VariantSelector from './variant-selector';
import CartService from '../cart/cart-service';

const Price = ({ currentVariant, isProductQuickView = false }) => {
  const compareAtPriceAmount = parseFloat(currentVariant.compareAtPrice.amount);
  const price = parseFloat(currentVariant.price.amount);
  let percentageDifference = 0;
  if (compareAtPriceAmount > 0) {
    percentageDifference =
      ((compareAtPriceAmount - price) / compareAtPriceAmount) * 100;
  }

  const isFinalSale = () =>
    currentVariant.finalSale;

  const formattedPercentageDifference =
    `${percentageDifference.toFixed(0)}% Off`;

  return (
    <div className={`
      flex gap-1 items-center
      ${isProductQuickView ? 'paragraph-4' : 'paragraph-2'}`
    }
    >
      {compareAtPriceAmount > 0 && (
        <del className={`
          text-grey-5 paragraph-2
          ${isProductQuickView ? 'paragraph-4' : 'paragraph-2'}`
        }
        >
          {formatMoney(currentVariant.compareAtPrice)}
        </del>
      )}
      <span className=
        {`
          ${isProductQuickView ? 'paragraph-4' : 'paragraph-2'}`
        }
      >
        {formatMoney(currentVariant.price)}
      </span>
      {compareAtPriceAmount > 0 && (
        <span className={`
          flex gap-0.5 items-center text-red-500 utility-4 pl-[6px]
          ${!isProductQuickView && 'mt-[3px]'}`
        }
        >
          {formattedPercentageDifference}
          {isFinalSale() &&
            <div className='flex gap-0.5 items-center'>
              <span className='mt-0.5'>
                <Icon name='dot' className='mt-0.5' />
              </span>
              <span>
                {window.TMW.Strings.product.finalSale}
              </span>
            </div>
          }
        </span>
      )}
    </div>
  );
};
const focusHandle = (focusedItem, e) => {
  if (!e.shiftKey && e.key === 'Tab') {
    e.preventDefault();
    focusedItem.focus();
  }
};

const ViewFullProduct = ({ isFocusClose ,currentVariant }) => (
  <div className="flex justify-center w-full pt-1">
    <a
    id='view_full_product'
      href={currentVariant.url}
      onKeyDown={(e) => focusHandle(isFocusClose, e)}
    >
      {window.TMW.Strings.quickView.viewFullProduct}
    </a>
  </div>
);

const AddToCartButton = ({
  product,
  currentVariant,
  engravingPayload,
  inputEngravingText,
  removeEngraving,
  isProductQuickView,
  isInStock,
  variantCartQuantity,
  disabled,
  setPreventedItem,
  setAddToCartMsg
}) => {
  const handleAddToCart = async (e) => {
    e.preventDefault();
    const giftCardNameInput = document.querySelector('#gift-card-name');
    const giftCardEmailInput = document.querySelector('#gift-card-email');
    const giftCardMessageInput = document.querySelector('#gift-card-message');
    const isGiftCard = giftCardNameInput
      && giftCardEmailInput && giftCardMessageInput;
    let attributes = [];

    if (isGiftCard) {
      attributes = [
        {
          key: 'Recipient name',
          value: giftCardNameInput.value,
        },
        {
          key: 'Recipient email address',
          value: giftCardEmailInput.value,
        }
      ];

      if (giftCardMessageInput.value) {
        attributes.push(
          {
            key: 'Recipient message',
            value: giftCardMessageInput.value,
          }
        );
      }
    } else if (engravingPayload && inputEngravingText) {
      const isEmbossing = engravingPayload?.attributes?.find(attr => attr.key === '_EmbossingProduct');
      const attributeName = isEmbossing ? 'Embossing' : 'Engraving';
      attributes = [
        {
          key: attributeName,
          value: inputEngravingText
        }
      ];
    } else if (
      product?.ignoreSafetyStock &&
      currentVariant?.availableForSale &&
      product?.preorderReleaseDate !== null) {
      attributes = [
        {
          key: 'pre-order release',
          value: product?.preorderReleaseDate,
        }
      ];
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: [
          {
            merchandiseId: currentVariant.id,
            quantity: 1,
            attributes,
          },
          ...(engravingPayload ? [engravingPayload] : [])
        ],
      },
    });

    // prevent add to cart if invalid gift card form
    if (isGiftCard) {
      if (!giftCardNameInput.value
        || !giftCardEmailInput.value
        || !isValidEmailCheck(giftCardEmailInput.value)) {
        return;
      }
    }

    if (inputEngravingText && engravingPayload) {
      removeEngraving(e);
    }

    try {
      setAddToCartMsg(`${product.title}
        ${currentVariant?.selectedOptions[0]?.value}
        added to your bag`)
      await CartService.add(addToCartEvent.detail.payload);
      setPreventedItem(null);
      setTimeout(()=> {
        setAddToCartMsg('')
      },500)

    } catch (cartError) {
      if (cartError.data?.item) {
        setPreventedItem(cartError.data?.item);
      }
    }

    if (isProductQuickView) {
      setTimeout(() => {
        const closeQuickViewPanel = new CustomEvent('close-panel:quick-view');
        document.dispatchEvent(closeQuickViewPanel);
      }, 400)
    }
  };

  const renderButtonText = () => {
    if (
      product?.ignoreSafetyStock &&
      currentVariant?.availableForSale &&
      product?.preorderReleaseDate !== null
    ) {
      return window.TMW.Strings.product.preorder;
    }

    if (product?.ignoreSafetyStock && currentVariant?.availableForSale) {
      return window.TMW.Strings.product.addToCart;
    }

    // safety stock considered here
    if (!isInStock
      || variantCartQuantity === currentVariant.inventoryQuantity) {
      return window.TMW.Strings.product.outOfStock;
    }

    if (!currentVariant || !currentVariant?.availableForSale) {
      return window.TMW.Strings.product.unavailable;
    }

    if (currentVariant?.availableForSale) {
      return window.TMW.Strings.product.addToCart;
    }

    if (!currentVariant?.availableForSale) {
      return window.TMW.Strings.product.outOfStock;
    }

    return window.TMW.Strings.product.outOfStock
  }
  return (
    <button
      type="submit"
      data-product-form-btn
      className="w-full button button--dark"
      disabled={!currentVariant.availableForSale || !isInStock || disabled}
      onClick={(e) => handleAddToCart(e)}
      aria-label={`${renderButtonText()}
      ${product.title}
      ${currentVariant?.selectedOptions[0].value}`}
    >
      {renderButtonText()}
    </button>
  );
};

const EngravingButton = ({
  currentVariant,
  engravingButtonRef,
  inputEngravingText,
  setInputEngravingText,
  removeEngraving,
  setEngravingPayload,
  engravingProduct,
  embossingProduct
}) => {

  const [isEmbossing, setIsEmbossing] = useState(false);

  const handleEngraving = (e) => {
    e.preventDefault();
    const engravingOpenedEvent = new CustomEvent('engraving-opened', {
      detail: {
        engravingRefElement: engravingButtonRef,
        currentVariant
      }
    });
    document.dispatchEvent(engravingOpenedEvent);
  };

  useEffect(() => {
    const handleEngravingApplyedEvent = (e) => {
      const { inputValue } = e.detail;
      const { engravingEventPayload } = e.detail;
      setInputEngravingText(inputValue);
      setEngravingPayload(engravingEventPayload);
    };

    document.addEventListener('engraving-applyed', handleEngravingApplyedEvent);

    return () => {
      document.removeEventListener('engraving-applyed', handleEngravingApplyedEvent);
    };
  }, []);

  useEffect(() => {
    const variantEmbossing = currentVariant?.embossing;
    if (embossingProduct && variantEmbossing) {
      setIsEmbossing(true);
    }
  },[embossingProduct]);

  return (
    <>
      {(currentVariant.engraving || isEmbossing) && !inputEngravingText &&
        <button
          className='w-full uppercase border button button--light border-grey-3'
          ref={engravingButtonRef}
          onClick={handleEngraving}
          data-engraving-button
        >
          {isEmbossing
            ? window.TMW.Strings.product.embossing.addEmbossing
            : window.TMW.Strings.product.engraving.addEngraving}
        </button>
      }
      {(currentVariant.engraving || isEmbossing) && inputEngravingText &&
        <div className='flex justify-between items-center border border-grey-3 h-[42px] px-4'>
          <div className='flex gap-2.5 font-medium'>
            <p>
              {isEmbossing
                ? window.TMW.Strings.product.embossing.embossingLabel
                : window.TMW.Strings.product.engraving.engravingLabel}
              {inputEngravingText}
              <span className='ml-2.5'>
                +{formatMoney({
                  amount: isEmbossing ?
                  embossingProduct.amount :
                  engravingProduct.amount,
                  currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                })}
              </span>
            </p>
          </div>
          <div className='flex gap-6'>
            <button
              className='uppercase cta'
              onClick={removeEngraving}
              data-remove-engraving
              aria-label={`${window.TMW.Strings.product.engraving.remove}
                engraving`}
              type="button"
              role="button"
            >
              {window.TMW.Strings.product.engraving.remove}
            </button>
            <button
              className='uppercase cta'
              ref={engravingButtonRef}
              onClick={handleEngraving}
              type="button"
              role="button"
              aria-label={`${window.TMW.Strings.product.engraving.edit}
                engraving`}
              data-edit-engraving
            >
              {window.TMW.Strings.product.engraving.edit}
            </button>
          </div>
        </div>
      }
    </>
  );
};

const giftFields = () => {
  const [isNameFieldError, setNameFieldError] = useState(false);
  const [isEmailFieldError, setEmailFieldError] = useState(false);
  const [isEmailValid, setEmailValid] = useState(true);
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const handleGiftInputChange = (event) => {
    if (event.target.value && event.target.name === 'name') {
      setNameFieldError(false);
    }

    if (event.target.value && event.target.name === 'email') {
      setEmailFieldError(false);
      setEmailValid(true);
    }
  }

  useEffect(() => {
    const addToCartBtn = document.querySelector('[data-product-form-btn]');
    const handleFieldsValidate = () => {
      if (!nameInputRef.current.value) {
        setNameFieldError(true);
        return;
      }

      if (!emailInputRef.current.value) {
        setEmailFieldError(true);
        return;
      }

      if (!isValidEmailCheck(emailInputRef.current.value)) {
        setEmailValid(false);
        return;
      }

      setEmailValid(true);

      nameInputRef.current.value = '';
      emailInputRef.current.value = '';
      messageInputRef.current.value = '';
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', handleFieldsValidate);
    }

    return () => {
      addToCartBtn.removeEventListener('click', handleFieldsValidate);
    }
  }, [])

  return (
    <div>
      <div className='flex flex-col mb-5'>
        <label htmlFor="gift-card-name" className='mb-1.5 uppercase paragraph-5 !font-medium'>
          {window.TMW.Strings.product.gift_card.name.label}
        </label>

        <input
          type="text"
          ref={nameInputRef}
          id="gift-card-name"
          onChange={handleGiftInputChange}
          className="h-[44px] border border-grey-3 placeholder:text-gray-5 px-[10px] py-[16px]"
          placeholder={window.TMW.Strings.product.gift_card.name.placeholder}
          name="name"
        />

        {isNameFieldError && (
          <div data-form-error className='mb-1.5 mt-1.5 paragraph-5 !font-medium text-error'>
            {window.TMW.Strings.product.gift_card.name.error}
          </div>
        )}
      </div>

      <div className='flex flex-col mb-5'>
        <label htmlFor="gift-card-email" className='mb-1.5 uppercase paragraph-5 !font-medium'>
          {window.TMW.Strings.product.gift_card.email.label}
        </label>

        <input
          type="email"
          ref={emailInputRef}
          id="gift-card-email"
          onChange={handleGiftInputChange}
          className="h-[44px] border border-grey-3 placeholder:text-gray-5 px-[10px] py-[16px]"
          placeholder={window.TMW.Strings.product.gift_card.email.placeholder}
          name="email"
        />

        {isEmailFieldError && (
          <div data-form-error className='mb-1.5 mt-1.5 paragraph-5 !font-medium text-error'>
            {window.TMW.Strings.product.gift_card.email.error}
          </div>
        )}

        {!isEmailValid && (
          <div data-form-error className='mb-1.5 mt-1.5 paragraph-5 !font-medium text-error'>
            {window.TMW.Strings.product.gift_card.email.invalid}
          </div>
        )}
      </div>

      <div className='flex flex-col'>
        <label htmlFor="gift-card-message" className='mb-1.5 uppercase paragraph-5 !font-medium'>
          {window.TMW.Strings.product.gift_card.message.label}
        </label>

        <input
          type="text"
          ref={messageInputRef}
          id="gift-card-message"
          className="h-[44px] border border-grey-3 placeholder:text-gray-5 px-[10px] py-[16px]"
          placeholder={window.TMW.Strings.product.gift_card.message.placeholder}
          name="message"
        />
      </div>
    </div>
  )
}

const ErrorMessage = ({ condition, message }) => {
  if (!condition) return null;
  return (
    <div className="flex items-start w-full gap-2 pb-3">
      <span className="inline-block w-1.5 h-1.5 mt-1.5 rounded-full bg-red-500 flex-shrink-0 flex-grow-0" />
      <span className="font-light text-red-500 paragraph-4">{message}</span>
    </div>
  );
};

const ProductGroup = (
  {
    isProductQuickView = false,
    quickViewVariantId = false,
    ...props
  }) => {
  const { product, cartItems, quickViewCloseRef } = props;
  const { productGroup } = props.product;
  const [currentTab, setCurrentTab] = useState(0);
  const [isInStock, setInStock] = useState(true); // < - includes safety stock
  const [isAddBtnDisabled, setAddBtnDisabled] = useState(false);
  const [isLowStock, setLowStock] = useState(false);
  const [variantCartQuantity, setVariantCartQuantity] = useState(0);
  const { engravingProduct } = props;
  const { embossingProduct } = props;
  const { isMobile } = useResponsive();
  const { safetyStockQuantity, lowStockWarningThreshold } = window.TMW;
  const [ignoreSafetyStock, setIgnoreSafetyStock] = useState(
    props.product.ignoreSafetyStock
  );
  const [preventedItem, setPreventedItem] = useState(null);
  const [addToCartMsg,setAddToCartMsg] = useState('')
  // Lookup variant based on selected options
  const [currentVariant, setCurrentVariant] = useState(
    product.variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) ===
        JSON.stringify(props.product.variantBySelectedOptions.selectedOptions),
    ),
  );
  const handleCurrentVariantUpdated = (e) => {
    const { currentProductData } = e.detail;
    setIgnoreSafetyStock(currentProductData.ignoreSafetyStock);
  }
  const preorderDetails = () => {
    if (ignoreSafetyStock &&
      currentVariant?.availableForSale &&
      product?.preorderReleaseDate !== null) {
      return (
        <div className="mt-4">
          <p className="paragraph-3">
            Expected ship date {product?.preorderReleaseDate}
          </p>
          {window.TMW.Strings.product.preorderDisclaimer && (
            <p className="paragraph-4">
              {window.TMW.Strings.product.preorderDisclaimer}
            </p>
          )}
        </div>
      )
    }
    return null;
  }
  useEffect(() => {
    document.addEventListener('currentVariantUpdated', handleCurrentVariantUpdated);
    return () => document.removeEventListener('currentVariantUpdated', handleCurrentVariantUpdated);
  }, []);

  const map = props?.collectionCodeMap?.map
  useEffect(() => {
    if (quickViewVariantId) {
      setCurrentVariant(
        product.variants.find((v) => v.id.includes(quickViewVariantId))
      );
    }
  }, [quickViewVariantId])

  const [stickyClass, setStickyClass] = useState('lg:top-0');

  useEffect(() => {
    let observeElement = document.querySelector('[data-add-to-cart-button]');
    if (isMobile) {
      observeElement = document.querySelector('[data-product-form]');
    }
    if (observeElement) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setStickyClass('hidden sm:block bottom-10 lg:bottom-auto lg:top-[90px]');
          } else {
            setStickyClass('hidden lg:block lg:top-0');
          }
        });
      }, {
        rootMargin: '-90px 0px 0px 0px',
        threshold: 0,
      });
      observer.observe(observeElement);
      return () => observer.disconnect();
    }
    return null;
  }, []);

  const variantSelectors = useMemo(
    () =>
      !props.product.hasOnlyDefaultVariant &&
      props.product.options.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          variants={props.product.variants}
          currentProduct={props.product}
          setPreventedItem={setPreventedItem}
        />
      )),
    [currentVariant],
  );

  const allSwatches = useMemo(() => {
    const allVariants = [];
    productGroup.forEach((p) => {
      p.variants.forEach((variant) => {
        allVariants.push(variant);
      })
    });
    const optionName = 'Color';
    const colorOption = product.options.find(option => option.name === 'Color');
    return (
      <VariantSelector
        key={`${product.id}-${optionName}`}
        currentVariant={currentVariant}
        option={colorOption}
        setCurrentVariant={setCurrentVariant}
        variants={allVariants}
        currentProduct={product}
        productGroup={productGroup}
        isProductQuickView={isProductQuickView}
        setPreventedItem={setPreventedItem}
      />
    )
  }, [currentVariant, productGroup]);

  const switchTab = (value,e) => {
    if(e.key === 'Enter' ||e.key === ' ') {
      setCurrentTab(value)
    }
  }

  const tabs = useMemo(() => {
    const collectionNumbers = productGroup.reduce((acc, pt) => {
      const collectionNumberTag = pt.tags.find(tag => tag.startsWith("Collection Number:"));
      if (collectionNumberTag) {
        const number = collectionNumberTag.split(':')[1].trim();
        acc.push(number);
      }
      return acc;
    }, []);

    return map?.reduce((acc, pg, index) => {
      const hasMatchingProduct = pg.ids.some(
        id => collectionNumbers.includes(id.toString())
      );

      if (hasMatchingProduct) {
        acc.push(
          <span
            key={pg.id}
            className={`cursor-pointer bg-grey-7 utility-4 p-[10px] ${currentTab === index + 1 ? 'border border-black' : ''}`}
            onClick={() => setCurrentTab(index + 1)}
            onKeyDown={(e) =>switchTab(index + 1,e) }
            role="tab"
            aria-selected={currentTab === index+1}
            tabIndex={'0'}
          >
            {pg.label}
          </span>
        );
      }
      return acc;
    }, []);
  }, [currentTab, productGroup, map]);


  const productGroupContent = useMemo(() => {
    if (currentTab === 0) return null;

    const selectedTab = map[currentTab - 1];
    const selectedTabIds = selectedTab?.ids;
    const selectedProduct = product;
    let multipleVariants = []

    productGroup.forEach(p => {
      const collectionTag = p.tags.find(tag => tag.startsWith("Collection Number:"));
      if (collectionTag) {
        const collectionNumber = collectionTag.split(':')[1];

        if (selectedTabIds.includes(collectionNumber) ||
          selectedTabIds.includes(parseInt(collectionNumber, 10))) {
          multipleVariants.push(...p.variants);
        }
      }
    });

    if (!multipleVariants) {
      multipleVariants = selectedProduct.variants
    }

    return (
      <div key={selectedProduct.id}>
        {selectedProduct.options.map((option) => (
          <VariantSelector
            key={option.name}
            currentVariant={currentVariant}
            option={option}
            setCurrentVariant={setCurrentVariant}
            variants={multipleVariants}
            currentProduct={selectedProduct}
            productGroup={productGroup}
            setPreventedItem={setPreventedItem}
          />
        ))}
      </div>
    );
  }, [currentTab, currentVariant, productGroup, allSwatches]);

  const price = useMemo(
    () =>
      <Price
        currentVariant={currentVariant}
        isProductQuickView={isProductQuickView}
      />,
    [currentVariant],
  );

  const [inputEngravingText, setInputEngravingText] = useState('');
  const [engravingPayload, setEngravingPayload] = useState('');


  const removeEngraving = (e) => {
    e.preventDefault();
    const removeEngravingEvent = new CustomEvent('engraving-removed');
    setInputEngravingText('');
    setEngravingPayload('');
    document.dispatchEvent(removeEngravingEvent);
  }

  const addToCartButton = useMemo(
    () => <AddToCartButton
      product={product}
      isInStock={isInStock}
      disabled={isAddBtnDisabled}
      variantCartQuantity={variantCartQuantity}
      currentVariant={currentVariant}
      engravingPayload={engravingPayload}
      inputEngravingText={inputEngravingText}
      setEngravingPayload={setEngravingPayload}
      setInputEngravingText={setInputEngravingText}
      removeEngraving={removeEngraving}
      isProductQuickView={isProductQuickView}
      cartItems={cartItems}
      setPreventedItem={setPreventedItem}
      setAddToCartMsg={setAddToCartMsg}
    />,
    [currentVariant, engravingPayload,
      isInStock, variantCartQuantity, isAddBtnDisabled],
  );

  const engravingButtonRef = useRef(null);

  const engravingButton = useMemo(
    () => <EngravingButton
      currentVariant={currentVariant}
      engravingButtonRef={engravingButtonRef}
      inputEngravingText={inputEngravingText}
      setInputEngravingText={setInputEngravingText}
      engravingProduct={engravingProduct}
      embossingProduct={embossingProduct}
      removeEngraving={removeEngraving}
      setEngravingPayload={setEngravingPayload}
    />,
    [currentVariant, engravingButtonRef, inputEngravingText, engravingPayload],
  );

  const stickyAddToCart = useMemo(() => {
    let selectedProduct = productGroup.find(
      (p) => p.variants.find(
        (v) => v.id === currentVariant.id)
    );
    if (!selectedProduct) {
      selectedProduct = product;
    }

    return (
      <div
        key={selectedProduct.id}
        className={`
          fixed
          ${stickyClass}
          w-full left-0 z-20 bg-white border-t border-t-primary
          transition-all duration-500
          lg:border-b lg:border-b-primary lg:bottom-[auto]`
        }
        data-sticky-add-to-cart
      >
        <div className='flex justify-between items-center px-5 py-2.5 lg:py-[5px] lg:px-10'>
          <div className='flex items-center gap-5'>
            <div className='hidden w-14 h-14 lg:block'>
              <Image
                {...currentVariant?.image?.src ? currentVariant?.image :
                  { src: product.images[0]?.src, alt: product.images[0]?.alt }}
                classes='w-14 h-14 aspect-square'
              />
            </div>
            <p className='hidden paragraph-1 lg:flex'>
              {selectedProduct.title}
            </p>
          </div>
          <div className='flex w-full gap-5 lg:w-auto'>
            <div className='hidden lg:flex'>
              {price}
            </div>
            <div className='w-full lg:w-auto lg:min-w-[340px]'>
              {addToCartButton}
            </div>
          </div>
        </div>
        {preventedItem && (
          <div className="pl-5">
            <ErrorMessage
              condition={preventedItem}
              message={window.TMW.Strings.product.preventPurchase.replace('{{ product_title }}', `${preventedItem?.product?.title} - ${preventedItem?.selectedOptions[0]?.value}`)}
            />
          </div>
        )}
      </div>
    );
  }, [currentTab,
    currentVariant,
    productGroup,
    stickyClass,
    product,
    preventedItem
  ]);

  const viewFullProduct = useMemo(
    () => <ViewFullProduct
      currentVariant={currentVariant}
      product={product}
      isInStock={isInStock}
      isFocusClose={quickViewCloseRef?.current}
    />,
    [currentVariant, isInStock],
  );

  const handleCurrentVariantDisable = (e) => {
    const isNeededVariant = currentVariant.id.includes(e.detail.payload.id);

    if (isNeededVariant) {
      setInStock(false);
    }
  }

  const handleCurrentVariantLowStock = (e) => {
    const isNeededVariant = currentVariant.id.includes(e.detail.payload.id);

    if (isNeededVariant) {
      setInStock(true);
      setLowStock(true);
    }
  }

  useEffect(() => {
    document.addEventListener("variant:out-of-stock", handleCurrentVariantDisable);
    document.addEventListener("variant:low-stock", handleCurrentVariantLowStock);

    return () => {
      document.removeEventListener("variant:out-of-stock", handleCurrentVariantDisable);
      document.removeEventListener("variant:low-stock", handleCurrentVariantLowStock);
    }
  }, [currentVariant]);

  useEffect(async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = currentVariant.id.split('/ProductVariant/')[1];
    const filteredCartItem = cartItems?.length
      && cartItems.find((item) => item.id === Number(id));

    if (searchParams.get('variant') !== id) {
      searchParams.set('variant', id);
      const newRelativePathQuery = `${window.location.pathname
        }?${searchParams.toString()}`;

      window.history.pushState(null, '', newRelativePathQuery);
    }

    if (filteredCartItem) {
      setVariantCartQuantity(filteredCartItem?.quantity || 0);
    } else {
      setVariantCartQuantity(0);
    }

    if (isProductQuickView) {
      const variant = await getVariantDataById(currentVariant.id);

      if (variant?.quantityAvailable) {
        currentVariant.inventoryQuantity = variant.quantityAvailable;
      }
    }

    const variantAvailable = ignoreSafetyStock ?
      currentVariant.availableForSale :
      currentVariant.inventoryQuantity
      - variantCartQuantity > safetyStockQuantity;

    const lowStock = !ignoreSafetyStock
      && currentVariant.inventoryQuantity - variantCartQuantity
      <= lowStockWarningThreshold;

    if (!variantAvailable) {
      setInStock(false);
      setAddBtnDisabled(true);
      return;
    }

    if (variantAvailable) {
      setInStock(true);
      setAddBtnDisabled(false);
    }

    if (lowStock) {
      setLowStock(true);
    }

    if (!lowStock) {
      setLowStock(false);
    }

  }, [currentVariant, variantCartQuantity]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const activeTabTitle = searchParams.get('tab');

    if (activeTabTitle) {
      const tabIndex =
        map.findIndex((m) => m.label === activeTabTitle);

      if (tabIndex !== -1) {
        setCurrentTab(tabIndex + 1);
      }
    }
  }, [productGroup]);

  return (
    <div className='relative'>
      <div className={
        `flex flex-col gap-2.5 pb-5
        ${!isProductQuickView && !product.isDigitalGiftCard && 'border-b border-grey-3'}
        `
      }
      >
        {!product.isGiftCard && price}
        {currentVariant.description &&
          <div className='pt-1 paragraph-4' dangerouslySetInnerHTML={{
            __html:
              currentVariant.description
          }} />
        }
      </div>
      {productGroup.length > 0 && !isProductQuickView &&
        <div className="pt-5 product-group-content">
          <div className='flex gap-[3px] overflow-auto whitespace-nowrap scrollbar-hide'>
            <span
              className={`cursor-pointer bg-grey-7 utility-4 p-[10px] ${currentTab === 0 ? 'border border-black' : ''}`}
              onClick={() => setCurrentTab(0)}
              onKeyDown={(e) =>switchTab(0,e) }
              tabIndex={'0'}
              aria-selected={currentTab === 0}
            >
              {window.TMW.Strings.product.allTag}
            </span>
            {tabs}
          </div>
          <div className='flex items-center gap-1 paragraph-2'>
            {currentTab === 0 ? allSwatches : productGroupContent}
          </div>
        </div>
      }
      {productGroup.length > 0 && isProductQuickView &&
        <div className='flex items-center gap-1 paragraph-2'>
          {allSwatches}
        </div>
      }
      {productGroup.length === 0 && variantSelectors}
      {product.isDigitalGiftCard && giftFields()}

      <div className={`pt-5 mt-5 sm:mt-0 
        ${isProductQuickView && 'flex flex-col items-center gap-4 z-10 static sm:fixed bottom-0 pb-2 w-[335px] border-t border-grey-3 bg-white'}`
      }
        data-add-to-cart-button
      >
        <div className='w-full pt-3 pb-3'>
          <ErrorMessage
            condition={isInStock && isLowStock && !isAddBtnDisabled}
            message={window.TMW.Strings.product.lowStock}
          />
          <ErrorMessage
            condition={!isInStock && !isAddBtnDisabled}
            message={window.TMW.Strings.product.outOfStockMessage}
          />
          <ErrorMessage
            condition={preventedItem}
            message={window.TMW.Strings.product.preventPurchase.replace('{{ product_title }}', `${preventedItem?.product?.title} - ${preventedItem?.selectedOptions[0]?.value}`)}
          />

          {(currentVariant.engraving || currentVariant.embossing)
            && !isProductQuickView && (
            <div aria-live="assertive" aria-atomic="true" className={`${isInStock && !isLowStock && !isAddBtnDisabled ? 'pb-2.5' : ''}`}>
              {engravingButton}
            </div>
          )}

          {addToCartButton}
          {isProductQuickView && viewFullProduct}
          {preorderDetails()}
        </div>
        {!isProductQuickView && stickyAddToCart}
        <div className="sr-only"
          data-add-to-cart-status
          aria-live="assertive"
          aria-atomic="true"
          role="status">{addToCartMsg}</div>
      </div>
    </div>
  );
};

export default ProductGroup;
